import { TextField as FieldDefinition, TextInputType } from '@bybeam/platform-types';
import TextInput, {
  TextInputValidationError,
} from '@platform-ui-common/components/TextInput/index.js';
import {
  GenericFormData,
  fieldUpdate,
  updateRepeated,
} from '@platform-ui-common/hooks/useForm/index.js';
import usePlaceholder from '@platform-ui-common/hooks/usePlaceholder.js';
import replacePlaceholders from '@platform-ui-common/utilities/placeholder.js';
import useAuth from '@platform-ui/auth/hooks/useAuth/index.js';
import { useEffect, useMemo, useState } from 'react';
import { FieldProps } from './types.js';

export default function TextField<FormData extends GenericFormData>({
  answers,
  dispatch,
  errors = {},
  field: { key, copy, inputType, props },
  labelPrefix,
  repeated,
  showManualAddressButton: _,
  showManualInput: __,
  setValidationOverrides,
  validation,
  ...rest
}: FieldProps<FieldDefinition, FormData>): JSX.Element {
  const { user } = useAuth();
  const placeholderData = usePlaceholder();
  const [error, setError] = useState<TextInputValidationError | null>(null);
  const answer = typeof answers[key] !== 'object' ? answers[key] : '';
  const id = `${key}${repeated?.index ? `-${repeated?.index}` : ''}`;

  const userName = user?.core?.name;

  const signatureError = useMemo(() => {
    if (inputType === TextInputType.Signature && answer && userName) {
      if ((answer as string) !== userName) {
        return `Please enter "${userName}"`;
      }
    }
    return null;
  }, [inputType, answer, userName]);

  const errorMessage = useMemo(() => {
    if (signatureError) return signatureError;

    if (answer) {
      switch (error) {
        case 'charCountMax': {
          return props?.characterCounter?.max
            ? `Invalid input. Must be less than ${props.characterCounter.max + 1} characters long`
            : 'Invalid input';
        }

        case 'charCountMin': {
          return props?.characterCounter?.min
            ? `Invalid input. Must be at least ${props.characterCounter.min} characters long`
            : 'Invalid input';
        }

        case 'charCountExact': {
          return props?.characterCounter?.exact
            ? `Invalid input. Must be exactly ${props.characterCounter.exact} characters long`
            : 'Invalid input';
        }

        case 'generalValidation': {
          return errors[key] && 'Invalid input';
        }

        default: {
          return;
        }
      }
    }
  }, [signatureError, error, props?.characterCounter, answer, errors[key], key]);

  const getHelperText = () => {
    if (inputType === TextInputType.Signature) return userName ?? '';
    return errorMessage
      ? errorMessage
      : replacePlaceholders(props?.helperText ?? '', placeholderData);
  };

  useEffect(() => {
    if (answer) {
      setValidationOverrides?.((overrides) => ({
        ...overrides,
        [id]: !!errorMessage,
      }));
    }

    return () => setValidationOverrides?.((overrides) => ({ ...overrides, [id]: false }));
  }, [errorMessage, setValidationOverrides, answer, id]);

  const label = labelPrefix ? `${labelPrefix} ${copy}` : copy;

  return (
    <div className="flex flex-col max-w-full lg:max-w-[36rem]">
      <TextInput
        id={id}
        label={label}
        type={inputType}
        value={answer ?? ''}
        onChange={(value): void =>
          repeated
            ? dispatch(updateRepeated(repeated.parentKey, repeated.index, key, value))
            : dispatch(fieldUpdate(key, value as FormData[typeof key]))
        }
        onError={(newError) => setError(newError)}
        error={(errors[key] as string) || errorMessage}
        required={validation?.required ?? true}
        {...props}
        helperText={getHelperText()}
        {...rest}
      />
    </div>
  );
}
